import {
  HIGH_ST_MIN_X,
  HIGH_ST_MAX_X,
  HIGH_ST_NORTH_FACE,
  HIGH_ST_SOUTH_FACE,
  CARRIAGEWAY_MIN_Z,
  CARRIAGEWAY_MAX_Z,
  STEM_MIN_X,
  STEM_MAX_X,
  STEM_MIN_Z,
  STEM_MAX_Z,
} from '../world/district'
import { INTERIOR_ISLAND_Z } from '../world/interiors'

type CtxFactory = () => AudioContext

function fillNoise(buf: AudioBuffer): void {
  const d = buf.getChannelData(0)
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
}

function noiseBuffer(ctx: AudioContext, secs: number): AudioBuffer {
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * secs), ctx.sampleRate)
  fillNoise(buf)
  return buf
}

// ── Surfaces ────────────────────────────────────────────────────────────────

export type Surface = 'asphalt' | 'pavement' | 'concrete' | 'interior'

/** Interiors live far up the -Z island; anything past the halfway mark is inside. */
const INTERIOR_Z_LINE = INTERIOR_ISLAND_Z / 2

/**
 * What the ground under a point sounds like, derived purely from the district
 * bounds. The carriageway is asphalt, the High Street footways are slabbed
 * pavement, the pedestrian stem is a poured-concrete apron, and anything on the
 * interior island is 'interior'. Pure and total — every point resolves to one
 * of the four, defaulting to pavement outside the named zones.
 */
export function surfaceAt(x: number, z: number): Surface {
  if (z < INTERIOR_Z_LINE) return 'interior'

  const inHighStX = x >= HIGH_ST_MIN_X && x <= HIGH_ST_MAX_X
  if (inHighStX) {
    if (z >= CARRIAGEWAY_MIN_Z && z <= CARRIAGEWAY_MAX_Z) return 'asphalt'
    if (z >= HIGH_ST_NORTH_FACE && z <= HIGH_ST_SOUTH_FACE) return 'pavement'
  }

  if (x >= STEM_MIN_X && x <= STEM_MAX_X && z >= STEM_MIN_Z && z <= STEM_MAX_Z) {
    return 'concrete'
  }

  return 'pavement'
}

interface StepTimbre {
  /** Bandpass centre frequency of the footfall's body. */
  freq: number
  /** Filter Q — higher is more pitched/tighter. */
  q: number
  /** Envelope decay, seconds. */
  decay: number
  /** Peak gain of a single footfall. */
  gain: number
}

const STEP_TIMBRES: Readonly<Record<Surface, StepTimbre>> = {
  // Hard, dull, low body — a heel on road.
  asphalt: { freq: 480, q: 0.9, decay: 0.085, gain: 0.09 },
  // Crisper slab tap with a touch more top.
  pavement: { freq: 820, q: 1.1, decay: 0.06, gain: 0.08 },
  // Bright, hard, ringy — poured concrete.
  concrete: { freq: 1250, q: 1.4, decay: 0.05, gain: 0.075 },
  // Soft, damped — carpet/wood underfoot indoors.
  interior: { freq: 300, q: 0.8, decay: 0.055, gain: 0.05 },
}

/** Metres of travel between footfalls. */
export const STRIDE_LENGTH = 0.75

// ── Footstep cadence ──────────────────────────────────────────────────────

/**
 * Turns a stream of horizontal positions into footfalls, one per STRIDE_LENGTH
 * travelled. Pure — no audio, fully unit-testable. A single large jump between
 * samples (a building teleport) is absorbed rather than machine-gunning a
 * burst of steps.
 */
export class FootstepCadence {
  private acc = 0
  private lastX = 0
  private lastZ = 0
  private has = false

  constructor(
    private readonly stride: number = STRIDE_LENGTH,
    /** A per-frame move larger than this is treated as a teleport, not walking. */
    private readonly teleportThreshold: number = 3,
  ) {}

  /**
   * Feed the current position; returns how many footfalls to emit this frame
   * (normally 0 or 1). Accumulates distance travelled and spends it in strides.
   */
  advance(x: number, z: number): number {
    if (!this.has) {
      this.has = true
      this.lastX = x
      this.lastZ = z
      return 0
    }
    const dx = x - this.lastX
    const dz = z - this.lastZ
    this.lastX = x
    this.lastZ = z
    const d = Math.sqrt(dx * dx + dz * dz)
    if (d > this.teleportThreshold) {
      this.acc = 0
      return 0
    }
    this.acc += d
    let steps = 0
    while (this.acc >= this.stride) {
      this.acc -= this.stride
      steps++
    }
    return steps
  }

  /** Move the reference point without accumulating (airborne, paused, teleport). */
  sync(x: number, z: number): void {
    this.has = true
    this.lastX = x
    this.lastZ = z
  }

  reset(): void {
    this.acc = 0
    this.has = false
  }
}

// ── Positional exterior sources ───────────────────────────────────────────

interface PointSource {
  x: number
  z: number
  /** Beyond this distance the source is silent. */
  maxDist: number
  /** Loudest gain (at zero distance). */
  baseGain: number
  gain: GainNode
  pan: StereoPannerNode
}

/** World anchors for the ambient point sources. */
const DATACENTRE_HUM = { x: 29.5, z: 11 }
const CAFE_MURMUR = { x: -13.5, z: 11 }

/** Centre of the carriageway strip — the loud part of the street. */
const CARRIAGEWAY_CENTRE_Z = (CARRIAGEWAY_MIN_Z + CARRIAGEWAY_MAX_Z) / 2

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/**
 * Distance from a point to the carriageway rectangle (0 when standing on it).
 * Exposure to traffic noise falls off with this.
 */
function distToCarriageway(x: number, z: number): number {
  const dx = Math.max(HIGH_ST_MIN_X - x, 0, x - HIGH_ST_MAX_X)
  const dz = Math.max(CARRIAGEWAY_MIN_Z - z, 0, z - CARRIAGEWAY_MAX_Z)
  return Math.sqrt(dx * dx + dz * dz)
}

export class AudioSystem {
  private ctx:    AudioContext | null = null
  private master: GainNode     | null = null
  private muted  = false
  private volume = 1

  // ── outdoor soundscape state ──
  private ambienceBuilt = false
  private bedGain:   GainNode        | null = null
  private bedFilter: BiquadFilterNode | null = null
  private point: PointSource[] = []
  private lfoPhase = 0
  private readonly cadence = new FootstepCadence()
  private stepFoot = false
  private prevY = 0
  private hasPrevY = false
  private vy = 0

  constructor(private readonly ctxFactory: CtxFactory = () => new AudioContext()) {}

  init(): void {
    if (this.ctx) {
      void this.ctx.resume()
      return
    }
    const ctx    = this.ctxFactory()
    this.ctx     = ctx
    const master = ctx.createGain()
    this.master  = master
    master.gain.value = this.muted ? 0 : this.volume
    master.connect(ctx.destination)
    this._startRoomTone(ctx, master)
  }

  private _startRoomTone(ctx: AudioContext, master: GainNode): void {
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 2)
    src.loop   = true

    const hp = ctx.createBiquadFilter()
    hp.type           = 'highpass'
    hp.frequency.value = 80

    const lp = ctx.createBiquadFilter()
    lp.type           = 'lowpass'
    lp.frequency.value = 280

    const g = ctx.createGain()
    g.gain.value = 0.04

    src.connect(hp)
    hp.connect(lp)
    lp.connect(g)
    g.connect(master)
    src.start()
  }

  setMuted(m: boolean): void {
    this.muted = m
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(m ? 0 : this.volume, this.ctx.currentTime, 0.05)
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
    if (this.master && this.ctx && !this.muted) {
      this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05)
    }
  }

  playUIClick(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const osc = ctx.createOscillator()
    const g   = ctx.createGain()
    osc.type           = 'sine'
    osc.frequency.value = 1200
    const now = ctx.currentTime
    g.gain.setValueAtTime(0.12, now)
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.04)
    osc.connect(g)
    g.connect(master)
    osc.start(now)
    osc.stop(now + 0.04)
  }

  playRunComplete(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    // Warm two-note chime: C5 then E5
    const notes: [number, number][] = [[523.25, 0], [659.25, 0.1]]
    for (const [freq, delay] of notes) {
      const osc = ctx.createOscillator()
      const g   = ctx.createGain()
      osc.type           = 'sine'
      osc.frequency.value = freq
      const t = ctx.currentTime + delay
      g.gain.setValueAtTime(0, t)
      g.gain.linearRampToValueAtTime(0.18, t + 0.015)
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.4)
      osc.connect(g)
      g.connect(master)
      osc.start(t)
      osc.stop(t + 1.4)
    }
  }

  playRunFailed(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 0.2)
    const lp = ctx.createBiquadFilter()
    lp.type           = 'lowpass'
    lp.frequency.value = 160
    const g   = ctx.createGain()
    const now = ctx.currentTime
    g.gain.setValueAtTime(0.45, now)
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.2)
    src.connect(lp)
    lp.connect(g)
    g.connect(master)
    src.start()
    src.stop(now + 0.2)
  }

  playKeyboardTick(
    deskPos: readonly [number, number, number],
    listenerPos: { readonly x: number; readonly z: number },
    listenerYaw: number,
  ): void {
    const { ctx, master } = this
    if (!ctx || !master) return

    const dx   = deskPos[0] - listenerPos.x
    const dz   = deskPos[2] - listenerPos.z
    const dist = Math.sqrt(dx * dx + dz * dz)
    const vol  = Math.max(0, 1 - dist / 28) * 0.1
    if (vol < 0.002) return

    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 0.025)

    const bp = ctx.createBiquadFilter()
    bp.type           = 'bandpass'
    bp.frequency.value = 2600 + (Math.random() * 600 - 300)
    bp.Q.value         = 2.5

    const pan = ctx.createStereoPanner()
    const angle = Math.atan2(dx, -dz) - listenerYaw
    pan.pan.value = Math.max(-1, Math.min(1, Math.sin(angle) * 0.75))

    const g   = ctx.createGain()
    const now = ctx.currentTime
    g.gain.setValueAtTime(vol, now)
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.025)

    src.connect(bp)
    bp.connect(g)
    g.connect(pan)
    pan.connect(master)
    src.start()
    src.stop(now + 0.025)
  }

  // ── Outdoor soundscape ────────────────────────────────────────────────────

  /**
   * Build the exterior ambience: a low looping traffic/wind bed plus a couple of
   * positional point sources (datacentre hum, cafe murmur). Idempotent and safe
   * to call before init() — it no-ops until there is a context. Started from the
   * app once audio is live.
   */
  startAmbience(): void {
    const { ctx, master } = this
    if (!ctx || !master || this.ambienceBuilt) return
    this.ambienceBuilt = true

    // ── Distant traffic + wind bed ──
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 3)
    src.loop = true

    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 45

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 420 // breathes via the LFO in update()
    this.bedFilter = lp

    const g = ctx.createGain()
    g.gain.value = 0 // driven by proximity in update()
    this.bedGain = g

    src.connect(hp)
    hp.connect(lp)
    lp.connect(g)
    g.connect(master)
    src.start()

    // ── Positional point sources ──
    this.point = [
      this._buildHum(ctx, master, DATACENTRE_HUM.x, DATACENTRE_HUM.z, 62, 26, 0.06),
      this._buildMurmur(ctx, master, CAFE_MURMUR.x, CAFE_MURMUR.z, 22, 0.05),
    ]
  }

  private _buildHum(
    ctx: AudioContext, master: GainNode,
    x: number, z: number, freq: number, maxDist: number, baseGain: number,
  ): PointSource {
    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.value = freq

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 180

    const g = ctx.createGain()
    g.gain.value = 0

    const pan = ctx.createStereoPanner()

    osc.connect(lp)
    lp.connect(g)
    g.connect(pan)
    pan.connect(master)
    osc.start()
    return { x, z, maxDist, baseGain, gain: g, pan }
  }

  private _buildMurmur(
    ctx: AudioContext, master: GainNode,
    x: number, z: number, maxDist: number, baseGain: number,
  ): PointSource {
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 3)
    src.loop = true

    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 520
    bp.Q.value = 0.7

    const g = ctx.createGain()
    g.gain.value = 0

    const pan = ctx.createStereoPanner()

    src.connect(bp)
    bp.connect(g)
    g.connect(pan)
    pan.connect(master)
    src.start()
    return { x, z, maxDist, baseGain, gain: g, pan }
  }

  /**
   * Per-frame driver: footsteps, the breathing ambience bed, and the positional
   * sources. Fed the listener transform from the game loop. Safe before init()
   * and before startAmbience(). Footsteps are distance-driven and surface-aware;
   * the exterior bed ducks and darkens when the listener is inside an interior.
   */
  update(
    dt: number,
    x: number,
    z: number,
    y: number,
    yaw: number,
  ): void {
    const { ctx, master } = this
    if (!ctx || !master) return

    const surface = surfaceAt(x, z)
    const inside = surface === 'interior'

    // ── vertical velocity estimate (for grounded / landing) ──
    if (this.hasPrevY) {
      const step = dt > 0 ? dt : 1 / 60
      this.vy = (y - this.prevY) / step
    }
    this.prevY = y
    this.hasPrevY = true
    const wasFalling = this.vy < -1.6
    const grounded = Math.abs(this.vy) < 0.6

    // ── footsteps ──
    if (grounded) {
      const steps = this.cadence.advance(x, z)
      for (let i = 0; i < steps; i++) this._footfall(surface)
      // A landing after a real fall: one soft thud on the grounded transition.
      if (wasFalling) this._landingThud(surface)
    } else {
      this.cadence.sync(x, z)
    }

    // ── ambience bed: proximity + slow LFO breath ──
    if (this.bedGain && this.bedFilter) {
      this.lfoPhase += dt * 0.09 // ~11 s period
      const breath = (Math.sin(this.lfoPhase * Math.PI * 2) + 1) / 2 // 0..1

      const exposure = clamp(1 - distToCarriageway(x, z) / 34, 0.12, 1)
      // Quieter the further up the stem you walk (away from the carriageway).
      const stemFade = clamp(1 - Math.max(0, z - CARRIAGEWAY_CENTRE_Z) / 40, 0.35, 1)
      const duck = inside ? 0.22 : 1
      const target = 0.05 * exposure * stemFade * duck * (0.75 + breath * 0.25)
      this.bedGain.gain.setTargetAtTime(target, ctx.currentTime, 0.4)

      const cutoff = inside ? 260 : 300 + breath * 520
      this.bedFilter.frequency.setTargetAtTime(cutoff, ctx.currentTime, 0.5)
    }

    // ── positional point sources ──
    for (const p of this.point) {
      const dx = p.x - x
      const dz = p.z - z
      const dist = Math.sqrt(dx * dx + dz * dz)
      const near = Math.max(0, 1 - dist / p.maxDist)
      const duck = inside ? 0.15 : 1
      p.gain.gain.setTargetAtTime(p.baseGain * near * near * duck, ctx.currentTime, 0.3)
      const angle = Math.atan2(dx, -dz) - yaw
      p.pan.pan.value = clamp(Math.sin(angle) * 0.8, -1, 1)
    }
  }

  private _footfall(surface: Surface): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const t = STEP_TIMBRES[surface]

    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, t.decay + 0.01)

    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = t.freq * (0.92 + Math.random() * 0.16) // pitch jitter
    bp.Q.value = t.q

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = t.freq * 3.2

    // Subtle left/right alternation so a walk does not centre-punch.
    this.stepFoot = !this.stepFoot
    const pan = ctx.createStereoPanner()
    pan.pan.value = (this.stepFoot ? 0.18 : -0.18) + (Math.random() * 0.08 - 0.04)

    const g = ctx.createGain()
    const now = ctx.currentTime
    const gain = t.gain * (0.82 + Math.random() * 0.32) // gain jitter
    g.gain.setValueAtTime(gain, now)
    g.gain.exponentialRampToValueAtTime(0.0008, now + t.decay)

    src.connect(bp)
    bp.connect(lp)
    lp.connect(g)
    g.connect(pan)
    pan.connect(master)
    src.start()
    src.stop(now + t.decay + 0.01)
  }

  private _landingThud(surface: Surface): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const t = STEP_TIMBRES[surface]

    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 0.16)
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = Math.min(220, t.freq)
    const g = ctx.createGain()
    const now = ctx.currentTime
    g.gain.setValueAtTime(0.26, now)
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.16)

    // A low body sine gives the thud its weight.
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = 72
    const og = ctx.createGain()
    og.gain.setValueAtTime(0.18, now)
    og.gain.exponentialRampToValueAtTime(0.001, now + 0.14)

    src.connect(lp)
    lp.connect(g)
    g.connect(master)
    osc.connect(og)
    og.connect(master)
    src.start()
    src.stop(now + 0.16)
    osc.start(now)
    osc.stop(now + 0.14)
  }

  // ── UI / feedback vocabulary ──────────────────────────────────────────────

  /** A tiny high blip as the reticle passes over something interactable. */
  playReticleBlip(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = 1760
    const now = ctx.currentTime
    g.gain.setValueAtTime(0.05, now)
    g.gain.exponentialRampToValueAtTime(0.0008, now + 0.05)
    osc.connect(g)
    g.connect(master)
    osc.start(now)
    osc.stop(now + 0.05)
  }

  /** A warm two-tone confirm for an [E] interaction. */
  playInteractConfirm(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const notes: [number, number][] = [[660, 0], [990, 0.06]]
    for (const [freq, delay] of notes) {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t = ctx.currentTime + delay
      g.gain.setValueAtTime(0, t)
      g.gain.linearRampToValueAtTime(0.12, t + 0.01)
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.16)
      osc.connect(g)
      g.connect(master)
      osc.start(t)
      osc.stop(t + 0.16)
    }
  }

  /** A soft door/enter whoosh for a building transition (enter or exit). */
  playTransition(): void {
    const { ctx, master } = this
    if (!ctx || !master) return
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx, 0.5)

    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.Q.value = 0.8
    const now = ctx.currentTime
    // Sweep the band downward — the "whoomp" of a door swinging past.
    bp.frequency.setValueAtTime(900, now)
    bp.frequency.exponentialRampToValueAtTime(180, now + 0.45)

    const g = ctx.createGain()
    g.gain.setValueAtTime(0, now)
    g.gain.linearRampToValueAtTime(0.16, now + 0.06)
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.45)

    src.connect(bp)
    bp.connect(g)
    g.connect(master)
    src.start()
    src.stop(now + 0.5)
  }

  get isReady(): boolean { return this.ctx !== null }

  destroy(): void {
    void this.ctx?.close()
    this.ctx   = null
    this.master = null
    this.bedGain = null
    this.bedFilter = null
    this.point = []
    this.ambienceBuilt = false
    this.cadence.reset()
    this.hasPrevY = false
  }
}

// Singleton used by UI components for click sounds
export const audioSystem = new AudioSystem()
